import React from 'react';

import { IDescriptions, TableCellDescriptions } from '../../components/TableCellDescriptions';
import type { DataChangeLogTableFragment } from '../../graphql/api/generated';
import { formatTimestamp } from '../../utils';
import type { IModelColumn } from '../typings';
import { UserModel } from '../User/model';

import model from './model';

interface IChangedFieldToColumnMapping {
  [key: string]: IModelColumn<any>;
}


function getDataChangeDescriptions(data: any, columnMapping: IChangedFieldToColumnMapping) {
  if (!data) {
    return {};
  }

  const descriptions: IDescriptions = {};
  const changedFields = Object.keys(data);

  for (const [fieldName, column] of Object.entries(columnMapping)) {
    if (
      !changedFields.includes(fieldName) ||
      !column ||
      (typeof column.enabled === 'function' && !column.enabled())
    ) {
      continue;
    }

    const title = column.titleString || column.title;

    if (typeof title === 'string') {
      const fieldValue = data[fieldName];

      if (fieldValue === null || fieldValue === undefined) {
        descriptions[title] = <></>; // Render blank description
      } else {
        // Only call column render if there is a field value
        descriptions[title] = column.render(data);
      }
    }
  }

  return descriptions;
}

export const createBeforeAndAfterColumns = (columnMapping: IChangedFieldToColumnMapping) => {
  const beforeColumn = model.createColumn<DataChangeLogTableFragment>({
    title: 'Before',
    render: ({ oldData }) => (
      <TableCellDescriptions descriptions={getDataChangeDescriptions(oldData, columnMapping)} />
    ),
  });

  const afterColumn = model.createColumn<DataChangeLogTableFragment>({
    title: 'After',
    render: ({ newData }) => (
      <TableCellDescriptions descriptions={getDataChangeDescriptions(newData, columnMapping)} />
    ),
  });

  return [beforeColumn, afterColumn];
};

export const changedByUserColumn = model.createColumn<DataChangeLogTableFragment>({
  title: 'Changed By',
  render: ({ changedByUser }) => UserModel.routes.renderRowLink(changedByUser),
});

export const changedAtColumn = model.createColumn<DataChangeLogTableFragment>({
  title: 'Timestamp',
  dataIndex: 'changedAt',
  sorter: true,
  defaultSortOrder: 'descend',
  sortDirections: ['descend', 'ascend', 'descend'], // Do not allow canceling sort
  showSorterTooltip: false,
  render: ({ changedAt }) => formatTimestamp(changedAt),
});
